/**
 * FOS Projection Jobs Hook
 *
 * Provides a unified interface for managing FOS projection jobs across environments:
 * - Development: Uses Celery tasks via existing /projections/compute endpoint
 * - Staging/Beta: Uses Kubernetes Jobs via /projections/compute-job endpoints
 *
 * The provider abstraction allows the UI to work identically regardless of backend.
 *
 * Supports two job types:
 * - FOS Projections: UMAP/HDBSCAN clustering of FOS decision embeddings
 * - Signal Projections: Projecting organization signals into FOS embedding space
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../utils/api-client';
import { config } from '../config';

// =============================================================================
// Types
// =============================================================================

export type ProjectionJobStatus =
  | 'pending'
  | 'running'
  | 'succeeded'
  | 'failed'
  | 'unknown';

export type ProjectionJobProvider = 'celery' | 'kubernetes';

export type ProjectionJobType = 'fos-projection' | 'signal-projection';

/**
 * Unified projection job status - works for both Celery and K8s
 */
export interface ProjectionJob {
  /** Job identifier (task_id for Celery, job name for K8s) */
  id: string;
  /** Human-readable name */
  name: string;
  /** Current status */
  status: ProjectionJobStatus;
  /** When the job was created/triggered */
  createdAt: string | null;
  /** When the job started running */
  startTime: string | null;
  /** When the job completed (success or failure) */
  completionTime: string | null;
  /** Duration in seconds if completed */
  durationSeconds: number | null;
  /** Provider type */
  provider: ProjectionJobProvider;
  /** Job type */
  jobType: ProjectionJobType;
  /** Parameters used for the projection */
  parameters?: {
    // FOS projection params
    n_neighbors?: number;
    min_dist?: number;
    min_cluster_size?: number;
    // Signal projection params
    organization_id?: string;
    days_back?: number;
    k_nearest?: number;
    limit?: number;
  };
  /** Error message if failed */
  error?: string;
}

/**
 * Request parameters for creating a FOS projection job
 */
export interface CreateFOSProjectionJobRequest {
  n_neighbors?: number;
  min_dist?: number;
  min_cluster_size?: number;
}

/**
 * Request parameters for creating a signal projection job
 */
export interface CreateSignalProjectionJobRequest {
  days_back?: number;
  k_nearest?: number;
  limit?: number;
}

/**
 * Union type for creating any projection job
 */
export type CreateProjectionJobRequest = CreateFOSProjectionJobRequest | CreateSignalProjectionJobRequest;

/**
 * Response from job creation
 */
export interface CreateProjectionJobResponse {
  status: string;
  job: ProjectionJob;
  message: string;
}

/**
 * Response from listing jobs
 */
export interface ProjectionJobListResponse {
  jobs: ProjectionJob[];
  total: number;
  provider: ProjectionJobProvider;
  /** CronJob info (K8s only) */
  cronJob?: {
    schedule: string | null;
    lastSchedule: string | null;
    suspended: boolean;
  };
}

// =============================================================================
// Provider Detection
// =============================================================================

/**
 * Detect which provider to use based on environment
 */
export function getProjectionJobProvider(): ProjectionJobProvider {
  const env = config.ENVIRONMENT?.toLowerCase() || 'development';

  // In staging/beta/production, use Kubernetes Jobs
  if (env === 'staging' || env === 'beta' || env === 'production') {
    return 'kubernetes';
  }

  // Default to Celery for development
  return 'celery';
}

/**
 * Check if K8s Jobs are available in current environment
 */
export function isKubernetesAvailable(): boolean {
  return getProjectionJobProvider() === 'kubernetes';
}

// =============================================================================
// API Functions
// =============================================================================

/**
 * Create a FOS projection job using the appropriate provider
 */
async function createFOSProjectionJob(
  request: CreateFOSProjectionJobRequest = {}
): Promise<CreateProjectionJobResponse> {
  const provider = getProjectionJobProvider();

  if (provider === 'kubernetes') {
    // K8s Job API
    const response = await apiClient.post('/v1/admin/fos/projections/compute-job', request);
    const data = response.data;

    return {
      status: data.status,
      message: data.message,
      job: {
        id: data.job_name,
        name: data.job_name,
        status: 'pending',
        createdAt: new Date().toISOString(),
        startTime: null,
        completionTime: null,
        durationSeconds: null,
        provider: 'kubernetes',
        jobType: 'fos-projection',
        parameters: data.parameters,
      },
    };
  } else {
    // Celery task API
    const params = new URLSearchParams();
    if (request.n_neighbors !== undefined) params.append('n_neighbors', request.n_neighbors.toString());
    if (request.min_dist !== undefined) params.append('min_dist', request.min_dist.toString());
    if (request.min_cluster_size !== undefined) params.append('min_cluster_size', request.min_cluster_size.toString());

    const queryString = params.toString();
    const response = await apiClient.post(
      queryString ? `/v1/admin/fos/projections/compute?${queryString}` : '/v1/admin/fos/projections/compute',
      {}
    );
    const data = response.data;

    return {
      status: data.status,
      message: data.message,
      job: {
        id: data.task_id,
        name: `celery-${data.task_id.substring(0, 8)}`,
        status: 'running',
        createdAt: new Date().toISOString(),
        startTime: new Date().toISOString(),
        completionTime: null,
        durationSeconds: null,
        provider: 'celery',
        jobType: 'fos-projection',
        parameters: data.parameters,
      },
    };
  }
}

/**
 * Create a signal projection job using the appropriate provider
 */
async function createSignalProjectionJob(
  request: CreateSignalProjectionJobRequest = {}
): Promise<CreateProjectionJobResponse> {
  const response = await apiClient.post('/v1/admin/fos/projections/signals/compute-job', request);
  const data = response.data;

  return {
    status: data.status,
    message: data.message,
    job: {
      id: data.job_name,
      name: data.job_name,
      status: data.provider === 'kubernetes' ? 'pending' : 'running',
      createdAt: new Date().toISOString(),
      startTime: data.provider === 'celery' ? new Date().toISOString() : null,
      completionTime: null,
      durationSeconds: null,
      provider: data.provider as ProjectionJobProvider,
      jobType: 'signal-projection',
      parameters: data.parameters,
    },
  };
}

/**
 * Create a projection job using the appropriate provider
 * @deprecated Use createFOSProjectionJob or createSignalProjectionJob directly
 */
async function createProjectionJob(
  request: CreateFOSProjectionJobRequest = {}
): Promise<CreateProjectionJobResponse> {
  return createFOSProjectionJob(request);
}

/**
 * List recent FOS projection jobs
 */
async function listFOSProjectionJobs(limit: number = 10): Promise<ProjectionJobListResponse> {
  const provider = getProjectionJobProvider();

  if (provider === 'kubernetes') {
    // K8s Jobs API
    const response = await apiClient.get(`/v1/admin/fos/projections/jobs?limit=${limit}`);
    const data = response.data;

    const jobs: ProjectionJob[] = data.jobs.map((job: any) => ({
      id: job.name,
      name: job.name,
      status: job.status as ProjectionJobStatus,
      createdAt: job.created_at,
      startTime: job.start_time,
      completionTime: job.completion_time,
      durationSeconds: job.duration_seconds,
      provider: 'kubernetes' as ProjectionJobProvider,
      jobType: 'fos-projection' as ProjectionJobType,
    }));

    return {
      jobs,
      total: data.total,
      provider: 'kubernetes',
      cronJob: data.cronjob_schedule
        ? {
            schedule: data.cronjob_schedule,
            lastSchedule: data.cronjob_last_schedule,
            suspended: data.cronjob_suspended ?? false,
          }
        : undefined,
    };
  } else {
    // Celery doesn't have a job listing API, so we return empty
    // In dev, jobs are fire-and-forget via Celery
    return {
      jobs: [],
      total: 0,
      provider: 'celery',
    };
  }
}

/**
 * List recent signal projection jobs
 */
async function listSignalProjectionJobs(limit: number = 10): Promise<ProjectionJobListResponse> {
  const response = await apiClient.get(`/v1/admin/fos/projections/signals/jobs?limit=${limit}`);
  const data = response.data;

  const jobs: ProjectionJob[] = data.jobs.map((job: any) => ({
    id: job.name,
    name: job.name,
    status: job.status as ProjectionJobStatus,
    createdAt: job.created_at,
    startTime: job.start_time,
    completionTime: job.completion_time,
    durationSeconds: job.duration_seconds,
    provider: data.provider as ProjectionJobProvider,
    jobType: 'signal-projection' as ProjectionJobType,
    parameters: {
      organization_id: job.organization_id,
    },
  }));

  return {
    jobs,
    total: data.total,
    provider: data.provider,
  };
}

/**
 * List recent projection jobs
 * @deprecated Use listFOSProjectionJobs or listSignalProjectionJobs directly
 */
async function listProjectionJobs(limit: number = 10): Promise<ProjectionJobListResponse> {
  return listFOSProjectionJobs(limit);
}

/**
 * Get status of a specific FOS projection job
 */
async function getFOSProjectionJobStatus(jobId: string): Promise<ProjectionJob | null> {
  const provider = getProjectionJobProvider();

  if (provider === 'kubernetes') {
    try {
      const response = await apiClient.get(`/v1/admin/fos/projections/job/${jobId}`);
      const data = response.data;

      return {
        id: data.name,
        name: data.name,
        status: data.status as ProjectionJobStatus,
        createdAt: data.created_at,
        startTime: data.start_time,
        completionTime: data.completion_time,
        durationSeconds: data.duration_seconds,
        provider: 'kubernetes',
        jobType: 'fos-projection',
      };
    } catch (error: any) {
      if (error?.response?.status === 404) {
        return null;
      }
      throw error;
    }
  } else {
    // Celery tasks don't have individual status endpoints in our API
    return null;
  }
}

/**
 * Get status of a specific signal projection job
 */
async function getSignalProjectionJobStatus(jobId: string): Promise<ProjectionJob | null> {
  try {
    const response = await apiClient.get(`/v1/admin/fos/projections/signals/job/${jobId}`);
    const data = response.data;

    return {
      id: data.name,
      name: data.name,
      status: data.status as ProjectionJobStatus,
      createdAt: data.created_at,
      startTime: data.start_time,
      completionTime: data.completion_time,
      durationSeconds: data.duration_seconds,
      provider: 'kubernetes',
      jobType: 'signal-projection',
      parameters: {
        organization_id: data.organization_id,
      },
    };
  } catch (error: any) {
    if (error?.response?.status === 404) {
      return null;
    }
    throw error;
  }
}

/**
 * Get status of a specific job
 * @deprecated Use getFOSProjectionJobStatus or getSignalProjectionJobStatus directly
 */
async function getProjectionJobStatus(jobId: string): Promise<ProjectionJob | null> {
  return getFOSProjectionJobStatus(jobId);
}

/**
 * Delete a FOS projection job (K8s only)
 */
async function deleteFOSProjectionJob(jobId: string): Promise<void> {
  const provider = getProjectionJobProvider();

  if (provider === 'kubernetes') {
    await apiClient.delete(`/v1/admin/fos/projections/job/${jobId}`);
  }
  // Celery tasks cannot be deleted after completion
}

/**
 * Delete a signal projection job (K8s only)
 */
async function deleteSignalProjectionJob(jobId: string): Promise<void> {
  await apiClient.delete(`/v1/admin/fos/projections/signals/job/${jobId}`);
}

/**
 * Delete a projection job (K8s only)
 * @deprecated Use deleteFOSProjectionJob or deleteSignalProjectionJob directly
 */
async function deleteProjectionJob(jobId: string): Promise<void> {
  return deleteFOSProjectionJob(jobId);
}

// =============================================================================
// React Query Hooks
// =============================================================================

/**
 * Hook to list projection jobs
 * Auto-refreshes every 10 seconds when jobs are running
 */
export function useProjectionJobs(limit: number = 10) {
  return useQuery({
    queryKey: ['admin', 'fos', 'projection-jobs', limit],
    queryFn: () => listProjectionJobs(limit),
    refetchInterval: (query) => {
      // Refresh faster when jobs are running
      const hasRunningJobs = query.state.data?.jobs.some(
        (job) => job.status === 'running' || job.status === 'pending'
      );
      return hasRunningJobs ? 5000 : 30000;
    },
  });
}

/**
 * Hook to get status of a specific job
 */
export function useProjectionJobStatus(jobId: string | null) {
  return useQuery({
    queryKey: ['admin', 'fos', 'projection-job', jobId],
    queryFn: () => (jobId ? getProjectionJobStatus(jobId) : null),
    enabled: !!jobId,
    refetchInterval: (query) => {
      const job = query.state.data;
      if (!job) return false;
      // Refresh while running
      return job.status === 'running' || job.status === 'pending' ? 5000 : false;
    },
  });
}

/**
 * Hook to create a projection job
 */
export function useCreateProjectionJob() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createProjectionJob,
    onSuccess: () => {
      // Invalidate job list and projection status
      queryClient.invalidateQueries({ queryKey: ['admin', 'fos', 'projection-jobs'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'fos', 'projections', 'status'] });
      queryClient.invalidateQueries({ queryKey: ['fos', 'projections'] });
    },
  });
}

/**
 * Hook to delete a projection job
 */
export function useDeleteProjectionJob() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteProjectionJob,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'fos', 'projection-jobs'] });
    },
  });
}

/**
 * Hook to get current provider info
 */
export function useProjectionJobProvider() {
  const provider = getProjectionJobProvider();

  return {
    provider,
    isKubernetes: provider === 'kubernetes',
    isCelery: provider === 'celery',
    providerLabel: provider === 'kubernetes' ? 'Kubernetes Job' : 'Celery Task',
  };
}

// =============================================================================
// Signal Projection Job Hooks
// =============================================================================

/**
 * Hook to list signal projection jobs
 * Auto-refreshes every 10 seconds when jobs are running
 */
export function useSignalProjectionJobs(limit: number = 10) {
  return useQuery({
    queryKey: ['admin', 'fos', 'signal-projection-jobs', limit],
    queryFn: () => listSignalProjectionJobs(limit),
    refetchInterval: (query) => {
      // Refresh faster when jobs are running
      const hasRunningJobs = query.state.data?.jobs.some(
        (job) => job.status === 'running' || job.status === 'pending'
      );
      return hasRunningJobs ? 5000 : 30000;
    },
  });
}

/**
 * Hook to get status of a specific signal projection job
 */
export function useSignalProjectionJobStatus(jobId: string | null) {
  return useQuery({
    queryKey: ['admin', 'fos', 'signal-projection-job', jobId],
    queryFn: () => (jobId ? getSignalProjectionJobStatus(jobId) : null),
    enabled: !!jobId,
    refetchInterval: (query) => {
      const job = query.state.data;
      if (!job) return false;
      // Refresh while running
      return job.status === 'running' || job.status === 'pending' ? 5000 : false;
    },
  });
}

/**
 * Hook to create a signal projection job
 */
export function useCreateSignalProjectionJob() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createSignalProjectionJob,
    onSuccess: () => {
      // Invalidate signal projection job list
      queryClient.invalidateQueries({ queryKey: ['admin', 'fos', 'signal-projection-jobs'] });
      // Also invalidate signal projections data
      queryClient.invalidateQueries({ queryKey: ['fos', 'signal-projections'] });
    },
  });
}

/**
 * Hook to delete a signal projection job
 */
export function useDeleteSignalProjectionJob() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteSignalProjectionJob,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'fos', 'signal-projection-jobs'] });
    },
  });
}
